/**
 * Team detail page (route /teams/:id). Inline-editable name + notes, a stats
 * strip, an attendance/placement sparkline, and the full per-event history
 * with a per-row unassign action (to pull a mis-grouped row back out) plus a
 * delete-team action.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertDialog,
  Badge,
  Button,
  Flex,
  Heading,
  IconButton,
  Table,
  Text,
  TextArea,
  TextField,
} from '@radix-ui/themes';
import Sparkline from './Sparkline';
import {
  deleteTeam,
  fetchTeamDetail,
  fmtDate,
  fmtNum,
  patchTeam,
  unassignFromTeam,
  type TeamDetailResponse,
} from './types';
import './Teams.css';

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="teams-stat-card">
    <div className="teams-stat-value">{value}</div>
    <Text size="1" color="gray">
      {label}
    </Text>
  </div>
);

const TeamDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const teamId = Number(id);
  const navigate = useNavigate();

  const [data, setData] = useState<TeamDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inline name editing
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  // Notes editing
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fetchTeamDetail(teamId);
      setData(d);
      setNameDraft(d.team.name);
      setNotesDraft(d.team.notes ?? '');
    } catch {
      setError('Failed to load team.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    if (Number.isFinite(teamId)) load();
  }, [teamId, load]);

  const commitName = async () => {
    const name = nameDraft.trim();
    setEditingName(false);
    if (!data || !name || name === data.team.name) {
      setNameDraft(data?.team.name ?? '');
      return;
    }
    const prev = data.team.name;
    setData({ ...data, team: { ...data.team, name } });
    try {
      await patchTeam(teamId, { name });
    } catch {
      setError('Failed to rename team.');
      setData((d) => (d ? { ...d, team: { ...d.team, name: prev } } : d));
      setNameDraft(prev);
    }
  };

  const commitNotes = async () => {
    if (!data) return;
    const notes = notesDraft.trim();
    if (notes === (data.team.notes ?? '')) {
      setEditingNotes(false);
      return;
    }
    setSavingNotes(true);
    try {
      await patchTeam(teamId, { notes });
      setData({ ...data, team: { ...data.team, notes: notes || null } });
      setEditingNotes(false);
    } catch {
      setError('Failed to save notes.');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleUnassign = async (liveTeamId: number) => {
    if (!data) return;
    if (
      !window.confirm(
        'Remove this night from the team? The per-event row becomes unassociated again.',
      )
    )
      return;
    const prevHistory = data.history;
    setData({ ...data, history: data.history.filter((h) => h.liveTeamId !== liveTeamId) });
    try {
      await unassignFromTeam(teamId, [liveTeamId]);
      await load();
    } catch {
      setError('Failed to unassign row.');
      setData((d) => (d ? { ...d, history: prevHistory } : d));
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteTeam(teamId);
      navigate('/teams');
    } catch {
      setError('Failed to delete team.');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // Chronological history for the sparkline (oldest → newest).
  const chrono = useMemo(() => {
    if (!data) return [];
    return [...data.history].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
  }, [data]);

  if (loading) {
    return (
      <div className="teams-page">
        <div className="teams-empty">Loading team…</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="teams-page">
        <div className="teams-error">{error}</div>
        <Button variant="soft" color="gray" onClick={() => navigate('/teams')}>
          Back to teams
        </Button>
      </div>
    );
  }

  if (!data) return null;

  const { team, stats, history } = data;

  return (
    <div className="teams-page">
      <Flex justify="between" align="start" mb="2" gap="3" wrap="wrap">
        <div style={{ flex: 1, minWidth: 240 }}>
          {editingName ? (
            <TextField.Root
              size="3"
              value={nameDraft}
              autoFocus
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={commitName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitName();
                else if (e.key === 'Escape') {
                  setNameDraft(team.name);
                  setEditingName(false);
                }
              }}
            />
          ) : (
            <Heading
              size="7"
              style={{ cursor: 'text' }}
              title="Click to rename"
              onClick={() => {
                setNameDraft(team.name);
                setEditingName(true);
              }}
            >
              {team.name}
            </Heading>
          )}
        </div>
        <Flex gap="2">
          <Button variant="soft" color="gray" onClick={() => navigate('/teams')}>
            Back
          </Button>
          <Button color="red" variant="soft" onClick={() => setConfirmDelete(true)}>
            Delete team
          </Button>
        </Flex>
      </Flex>

      {/* Notes */}
      <div style={{ marginBottom: '0.5rem' }}>
        {editingNotes ? (
          <Flex direction="column" gap="2" style={{ maxWidth: 520 }}>
            <TextArea
              value={notesDraft}
              placeholder="Notes about this team…"
              onChange={(e) => setNotesDraft(e.target.value)}
              autoFocus
            />
            <Flex gap="2">
              <Button size="1" onClick={commitNotes} disabled={savingNotes}>
                {savingNotes ? 'Saving…' : 'Save notes'}
              </Button>
              <Button
                size="1"
                variant="soft"
                color="gray"
                onClick={() => {
                  setNotesDraft(team.notes ?? '');
                  setEditingNotes(false);
                }}
              >
                Cancel
              </Button>
            </Flex>
          </Flex>
        ) : team.notes ? (
          <Text
            size="2"
            color="gray"
            style={{ cursor: 'text' }}
            title="Click to edit notes"
            onClick={() => setEditingNotes(true)}
          >
            {team.notes}
          </Text>
        ) : (
          <Text
            size="2"
            color="gray"
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => setEditingNotes(true)}
          >
            + Add notes
          </Text>
        )}
      </div>

      {error && <div className="teams-error">{error}</div>}

      {/* Stats strip */}
      <div className="teams-stat-strip">
        <Stat label="Visits" value={stats.visits} />
        <Stat label="Avg players" value={fmtNum(stats.avgPlayers)} />
        <Stat label="Avg placement" value={fmtNum(stats.avgPlacement)} />
        <Stat label="Wins" value={stats.wins} />
        <Stat label="Podiums" value={stats.podiums} />
        <Stat label="First seen" value={fmtDate(stats.firstSeen)} />
        <Stat label="Last seen" value={fmtDate(stats.lastSeen)} />
      </div>

      {/* Sparklines */}
      {chrono.length > 0 && (
        <Flex gap="5" mb="4" wrap="wrap">
          <Sparkline
            label="Attendance (players over time)"
            mode="bars"
            values={chrono.map((h) => h.playerCount)}
          />
          <Sparkline
            label="Placement over time"
            mode="line"
            color="var(--blue-9)"
            values={chrono.map((h) => h.placement)}
          />
        </Flex>
      )}

      {/* History */}
      <Heading size="3" mb="2">
        History
      </Heading>
      {history.length === 0 ? (
        <div className="teams-empty">
          <Text size="2" color="gray">
            No per-event rows are associated with this team yet.
          </Text>
        </div>
      ) : (
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Date</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Quiz</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Name used</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="center">Players</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="center">Total</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="center">Place</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell />
              <Table.ColumnHeaderCell align="right" />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {[...history]
              .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
              .map((h) => (
                <Table.Row key={h.liveTeamId}>
                  <Table.RowHeaderCell>{fmtDate(h.date)}</Table.RowHeaderCell>
                  <Table.Cell>
                    <Text color="gray">{h.quizName}</Text>
                  </Table.Cell>
                  <Table.Cell>{h.teamNameUsed}</Table.Cell>
                  <Table.Cell align="center">{h.playerCount ?? '—'}</Table.Cell>
                  <Table.Cell align="center">{h.grandTotal ?? '—'}</Table.Cell>
                  <Table.Cell align="center">
                    {h.placement !== null ? (
                      <Badge
                        variant="soft"
                        color={h.placement === 1 ? 'green' : h.placement <= 3 ? 'amber' : 'gray'}
                      >
                        {h.placement}
                        {h.totalTeams !== null ? ` / ${h.totalTeams}` : ''}
                      </Badge>
                    ) : (
                      '—'
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {h.leftEarly && (
                      <Badge color="orange" variant="surface">
                        left early
                      </Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell align="right">
                    <IconButton
                      size="1"
                      variant="ghost"
                      color="red"
                      title="Unassign this night from the team"
                      onClick={() => handleUnassign(h.liveTeamId)}
                    >
                      ✕
                    </IconButton>
                  </Table.Cell>
                </Table.Row>
              ))}
          </Table.Body>
        </Table.Root>
      )}

      <AlertDialog.Root open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialog.Content maxWidth="420px">
          <AlertDialog.Title>Delete team</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Delete “{team.name}”? Its per-event rows become unassociated again (their scores and
            history are not deleted).
          </AlertDialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <AlertDialog.Cancel>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </AlertDialog.Cancel>
            <Button color="red" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete team'}
            </Button>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </div>
  );
};

export default TeamDetail;
